/**
 * Wire schemas for the driver's-license barcode scan surface (`BACKLOG.md` C19,
 * `docs/decisions/patient-license-scan-scope.md`). Three audiences, mirroring `kiosk.ts`'s own split:
 *
 *   - **Staff** (`POST /api/license-scans/tokens`, `POST /api/license-scans/tokens/:id/revoke`) —
 *     behind the ordinary staff session and `patient:read`, the same capability choice `kiosk.ts`'s
 *     own header argues for its own token issue/revoke pair: issuing a token does not itself write to
 *     the patient record.
 *   - **Kiosk capture** (`POST /api/license-scans/capture`) — unauthenticated in the ordinary sense
 *     (no staff session), authenticated instead by the `license_scan`-purpose bearer token from the
 *     create response above. Carries only the already-decoded, already-AAMVA-parsed candidate field
 *     values (`licenseScanParser.ts`'s `parseAamvaLicenseBarcode` runs entirely client-side, in
 *     `web/src/routes/kiosk/LicenseScanCapturePage.tsx`) — never image bytes, never the raw barcode
 *     text, per the decision doc's §3: only the decoded structured payload leaves the device.
 *   - **Staff review** (`GET /api/patients/:patientId/license-scans`,
 *     `POST /api/patients/:patientId/license-scans/:scanId/resolve`) — the ordinary staff session,
 *     `patient:write` (list) is deliberately not required for the read; see
 *     `server/src/clinical/licenseScanReviewRoutes.ts`'s own header for the capability split.
 */

import { z } from 'zod';

import {
  licenseScanExtractedFieldsSchema,
  licenseScanFieldKeySchema,
} from './licenseScanFields.ts';

// --- Staff: issue / revoke -------------------------------------------------------------------------

export const createLicenseScanTokenRequestSchema = z.strictObject({
  patientId: z.uuid(),
});
export type CreateLicenseScanTokenRequest = z.infer<typeof createLicenseScanTokenRequestSchema>;

export const licenseScanTokenResponseSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  /** Present only on the create response — the one and only time the raw bearer value is ever sent,
   * matching `kioskTokenResponseSchema`'s own `token` field. */
  token: z.string().optional(),
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type LicenseScanTokenResponse = z.infer<typeof licenseScanTokenResponseSchema>;

export const createLicenseScanTokenResponseSchema = z.strictObject({
  token: licenseScanTokenResponseSchema,
});
export type CreateLicenseScanTokenResponse = z.infer<typeof createLicenseScanTokenResponseSchema>;

export const LICENSE_SCAN_TOKEN_ERROR_CODES = [
  'invalid_request',
  'not_found',
  'forbidden',
  'audit_unavailable',
] as const;
export type LicenseScanTokenErrorCode = (typeof LICENSE_SCAN_TOKEN_ERROR_CODES)[number];

// --- Kiosk: capture ----------------------------------------------------------------------------------

/** Every field is optional — a real scan rarely decodes all eight, per `licenseScanFields.ts`'s own
 * comment — but the object as a whole must not be entirely empty: a capture with nothing extracted is
 * not a pending review worth queuing, it is a failed scan the kiosk screen should let the patient
 * retry instead of submitting. */
export const captureLicenseScanRequestSchema = z.strictObject({
  extractedFields: licenseScanExtractedFieldsSchema.refine(
    (fields) => Object.keys(fields).length > 0,
    { message: 'extractedFields must contain at least one field' },
  ),
});
export type CaptureLicenseScanRequest = z.infer<typeof captureLicenseScanRequestSchema>;

export const captureLicenseScanResponseSchema = z.strictObject({
  id: z.uuid(),
  status: z.literal('pending'),
});
export type CaptureLicenseScanResponse = z.infer<typeof captureLicenseScanResponseSchema>;

export const LICENSE_SCAN_CAPTURE_ERROR_CODES = [
  'invalid_request',
  'license_scan_token_invalid',
  'license_scan_token_expired',
  'audit_unavailable',
] as const;
export type LicenseScanCaptureErrorCode = (typeof LICENSE_SCAN_CAPTURE_ERROR_CODES)[number];

// --- Staff: review -------------------------------------------------------------------------------

export const LICENSE_SCAN_STATUSES = ['pending', 'confirmed', 'rejected'] as const;
export type LicenseScanStatus = (typeof LICENSE_SCAN_STATUSES)[number];
export const licenseScanStatusSchema = z.enum(LICENSE_SCAN_STATUSES);

export const licenseScanCandidateResponseSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  extractedFields: licenseScanExtractedFieldsSchema,
  status: licenseScanStatusSchema,
  confirmedFields: z.array(licenseScanFieldKeySchema).nullable(),
  resolvedByUserId: z.uuid().nullable(),
  resolvedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type LicenseScanCandidateResponse = z.infer<typeof licenseScanCandidateResponseSchema>;

export const listLicenseScansResponseSchema = z.strictObject({
  scans: z.array(licenseScanCandidateResponseSchema),
});
export type ListLicenseScansResponse = z.infer<typeof listLicenseScansResponseSchema>;

/**
 * A staff field-by-field review decision — the decision doc's §2 "field-by-field accept/keep
 * decision," not a single whole-record confirm button. `acceptedFields` may be empty even for
 * `confirmed`: every field already agreed with what was on file, or staff chose to keep the existing
 * value for every one of them, and the review is still being closed out as reviewed.
 */
export const resolveLicenseScanRequestSchema = z.discriminatedUnion('status', [
  z.strictObject({
    status: z.literal('confirmed'),
    acceptedFields: z.array(licenseScanFieldKeySchema),
  }),
  z.strictObject({ status: z.literal('rejected') }),
]);
export type ResolveLicenseScanRequest = z.infer<typeof resolveLicenseScanRequestSchema>;

export const resolveLicenseScanResponseSchema = z.strictObject({
  scan: licenseScanCandidateResponseSchema,
});
export type ResolveLicenseScanResponse = z.infer<typeof resolveLicenseScanResponseSchema>;

export const LICENSE_SCAN_REVIEW_ERROR_CODES = [
  'invalid_request',
  'not_found',
  'already_resolved',
  'chart_access_denied',
  'audit_unavailable',
] as const;
export type LicenseScanReviewErrorCode = (typeof LICENSE_SCAN_REVIEW_ERROR_CODES)[number];
